import {
  FeedbackDefaultFilters,
  FeedbackDefaultMultiFilter,
} from "content/DefaultFilter";
import { FeedbackTableColumns } from "content/TableColumns";
import { TableActionLoader, TableRowLoader } from "components/loaders";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "assets/svg";
import useFetch from "hooks/useFetch";
import * as Components from "components";
import { debounce, formatThousand } from "utils";
import { sortOptions } from "content/SortDropdown";
import { Avatar, Space } from "antd";
import { FeedbackTableMenuOptions } from "content/Dropdown";
import { useNavigate } from "react-router";
import {
  archiveFeedback,
  archiveMultipleFeedbacks,
  deleteFeedback,
  deleteMultipleFeedbacks,
  unarchiveFeedback,
  unarchiveMultipleFeedbacks,
} from "services/feedback.service";
import { toast } from "react-toastify";
import { DeleteModal, ModalLayout } from "components/modals";

const AllFeedbacks = () => {
  const [checkedTableMenuColumn, setCheckedTableMenuColumn] =
    useState(FeedbackTableColumns);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(FeedbackDefaultFilters);
  const [search, setSearch] = useState("");
  const [isRerender, setIsRerender] = useState();
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [feedbackToDelete, setFeedbackToDelete] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [fullFeedbackText, setFullFeedbackText] = useState("");
  const navigate = useNavigate();
  const { data, loading } = useFetch(`/admin/feedbacks`, filter, [
    filter,
    isRerender,
  ]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      handleFilter("search", value);
    }, 500),
    []
  );

  // Handle Search change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleFilter = (name, value) => {
    if (name === "filter") {
      setFilter((prev) => ({
        ...prev,
        ...value,
        page: 1,
      }));
    } else if (name === "pagination") {
      setFilter((prev) => ({
        ...prev,
        page: value.current,
        limit: value.pageSize,
      }));
    } else {
      setFilter((prev) => ({
        ...prev,
        [name]: value,
        page: 1,
      }));
    }
    setSelectedRowKeys([]);
  };

  const handleFilterChange = (selectedFilters) => {
    const combinedFilters = Object.values(selectedFilters).flat();

    const formatedFilters = combinedFilters.reduce(
      (acc, item) => {
        if (item === "Landlord" || item === "Tenant") {
          acc.role = acc.role ? `${acc.role},${item}` : item;
        } else if (
          item === "Disappointed" ||
          item === "Poor" ||
          item === "Average" ||
          item === "Good" ||
          item === "Fantastic"
        ) {
          acc.experience = acc.experience ? `${acc.experience},${item}` : item;
        } else if (item === "isArchived") {
          acc.isArchived = true;
        }
        return acc;
      },
      { role: "", experience: "", isArchived: false }
    );
    // update filters
    handleFilter("filter", formatedFilters);
  };

  const handleMenuClick = async (key, record) => {
    if (key === "view") {
      // navigate(`/feedbacks/${record._id}`);
    } else if (key === "archive") {
      const toastId = toast.loading("Archiving Feedback...");
      try {
        await archiveFeedback(record._id);
        toast.update(toastId, {
          render: "Feedback archived successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        setIsRerender((prev) => !prev);
      } catch (error) {
        console.error("Error archiving feedback:", error);
        toast.update(toastId, {
          render: error.response?.data?.message || "Failed to archive feedback",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } else if (key === "unarchive") {
      const toastId = toast.loading("Unarchiving Feedback...");
      try {
        await unarchiveFeedback(record._id);
        toast.update(toastId, {
          render: "Feedback unarchived successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        setIsRerender((prev) => !prev);
      } catch (error) {
        console.error("Error unarchiving feedback:", error);
        toast.update(toastId, {
          render:
            error.response?.data?.message || "Failed to unarchive feedback",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } else if ("delete") {
      const { firstName } = record.user;
      setDeleteId(record._id);
      setIsDeleteModal(true);
      setFeedbackToDelete(`${firstName}'s Feedback`);
    }
  };

  const DeleteFeedback = async () => {
    if (selectedRowKeys.length) {
      return DeleteMultipleFeedbacks(selectedRowKeys);
    } else {
      try {
        await deleteFeedback(deleteId);
        toast.success("Feedback deleted successfully");
        setIsRerender((prev) => !prev);
      } catch (error) {
        console.error("Error deleting feedback:", error);
        toast.error(
          error.response?.data?.message || "Failed to delete feedback"
        );
      }
    }
  };

  const handleArchiveMultipleFeedbacks = async (ids) => {
    if (filter.isArchived) return UnarchiveMultipleFeedbacks(ids);
    return ArchiveMultipleFeedbacks(ids);
  };

  const DeleteMultipleFeedbacks = async (ids) => {
    if (!ids || ids.length === 0) {
      toast.info("No feedbacks selected");
      return;
    }

    try {
      const response = await deleteMultipleFeedbacks(ids);

      toast.success(
        response.data.message ||
          `Successfully deleted ${ids.length} feedback(s)`
      );

      setIsRerender((prev) => !prev);

      setSelectedRowKeys([]);

      return true;
    } catch (error) {
      console.error("Error deleting multiple feedbacks:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to delete the selected feedbacks"
      );
      return false;
    }
  };

  // Function to archive multiple feedbacks
  const ArchiveMultipleFeedbacks = async (ids) => {
    if (!ids || ids.length === 0) {
      toast.info("No feedbacks selected");
      return;
    }

    const toastId = toast.loading("Archiving Feedbacks...");

    try {
      const response = await archiveMultipleFeedbacks(ids);

      const count = response.data.stats?.modified || ids.length;
      toast.update(toastId, {
        render: `Successfully archived ${count} feedback(s)`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      setIsRerender((prev) => !prev);
      setSelectedRowKeys([]);

      return true;
    } catch (error) {
      console.error("Error archiving multiple feedbacks:", error);
      toast.update(toastId, {
        render:
          error.response?.data?.message ||
          "Failed to archive the selected feedbacks",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      return false;
    }
  };

  const UnarchiveMultipleFeedbacks = async (ids) => {
    if (!ids || ids.length === 0) {
      toast.info("No feedbacks selected");
      return;
    }

    const toastId = toast.loading("Unarchiving Feedbacks...");

    try {
      const response = await unarchiveMultipleFeedbacks(ids);

      const count = response.data.stats?.modified || ids.length;
      toast.update(toastId, {
        render: `Successfully unarchived ${count} feedback(s)`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      setIsRerender((prev) => !prev);

      setSelectedRowKeys([]);

      return true;
    } catch (error) {
      console.error("Error unarchiving multiple feedbacks:", error);
      toast.update(toastId, {
        render:
          error.response?.data?.message ||
          "Failed to unarchive the selected feedbacks",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      return false;
    }
  };

  const onRowSelectionChange = (selectedKeys) => {
    setSelectedRowKeys(selectedKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
  };

  const handleShowFullFeedback = (text) => {
    setFullFeedbackText(text);
    setIsFeedbackModalOpen(true);
  };

  const FeedbackCell = ({ text }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isOverflowing);
      }
    }, [text]);

    return (
      <div className="relative">
        <div
          ref={textRef}
          className="whitespace-nowrap overflow-hidden text-ellipsis max-w-[250px]"
        >
          {text}
        </div>
        {isTruncated && (
          <button
            className="text-blue-500 hover:text-blue-700 text-sm mt-1"
            onClick={() => handleShowFullFeedback(text)}
          >
            Read More
          </button>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { firstName, lastName, profileImg } = record.user;

        return (
          <div className="flex items-center gap-3">
            {profileImg ? (
              <Avatar src={profileImg} alt={firstName} size="large" />
            ) : (
              <Components.InitialsAvatar
                firstName={firstName}
                lastName={lastName}
              />
            )}
            <span>{`${firstName} ${lastName}`}</span>
          </div>
        );
      },
    },
    {
      title: "CONTACT INFORMATION",
      dataIndex: "contactInfo",
      loader: <TableRowLoader />,
      render: (_, record) => {
        const { phone, email } = record.user;
        return (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3">
              <img
                src={Icons.MobilePhoneIcon}
                alt=""
                className="size-5 shrink-0"
              />
              <span>{phone}</span>{" "}
            </div>
            <div className="flex items-center gap-3">
              <img src={Icons.EmailIcon} alt="" className="size-5 shrink-0" />
              <span>{email}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "ROLE",
      dataIndex: "role",
      loader: <TableRowLoader />,
      render: (_, record) => {
        return (
          <div>
            <div>{record.role}</div>
          </div>
        );
      },
    },
    {
      title: "FEEDBACK",
      dataIndex: "feedback",
      loader: <TableRowLoader />,
      render: (_, record) => <FeedbackCell text={record.description || ""} />,
    },
    {
      title: "EXPERIENCE",
      dataIndex: "experience",
      loader: <TableRowLoader />,
      render: (_, record) => {
        return (
          <div>
            <div>{record.rating}</div>
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "action",
      loader: <TableActionLoader />,
      render: (_, record) => (
        <Space size="middle" className="flex justify-center">
          <Components.DropDownMenu
            menuItems={
              record.isArchived
                ? FeedbackTableMenuOptions.filter(
                    (option) => option.key !== "archive"
                  )
                : FeedbackTableMenuOptions.filter(
                    (option) => option.key !== "unarchive"
                  )
            }
            onMenuClick={(actionKey) => handleMenuClick(actionKey, record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="flex flex-col border dark:border-gray-800 my-5 2xl:my-10 mx-5 2xl:mx-14 rounded-lg shadow-lg dark:bg-[#181818]">
        <div className="w-full flex items-center px-5 py-3">
          <div className="w-full flex gap-5">
            <div className="flex gap-5 items-center w-full xl:w-2/3">
              <div id="step3" className="w-full xl:w-[400px]">
                <Components.SearchField
                  mobilePlaceholder="Search"
                  placeholder="Search User by Name, Email or Date"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
              <Components.SortDropdown
                sortOptions={sortOptions}
                propertyType={true}
                onSortChange={(value) => handleFilter("sortBy", value)}
              />
              <Components.FilterDropdown
                columnsData={FeedbackDefaultMultiFilter}
                onFilterChange={handleFilterChange}
              />
            </div>
            {selectedRowKeys.length > 0 && (
              <div className="flex items-center gap-4">
                <Components.ArchiveButton
                  onClick={() =>
                    handleArchiveMultipleFeedbacks(selectedRowKeys)
                  }
                >
                  {filter.isArchived ? "Unarchive" : "Archive"}
                </Components.ArchiveButton>

                <Components.DeleteButton
                  onClick={() => setIsDeleteModal(true)}
                />
              </div>
            )}
            <div className="flex items-center justify-end xl:w-1/2">
              <Components.TableCustomizeColumnMenu
                setCheckedTableMenuColumn={setCheckedTableMenuColumn}
                checkedTableMenuColumn={checkedTableMenuColumn}
              />
            </div>
          </div>
        </div>

        <div id="step4">
          <Components.CustomTable
            columns={columns.filter(
              (column) =>
                column.dataIndex === "action" ||
                checkedTableMenuColumn.some(
                  (col) => col.dataIndex === column.dataIndex && col.visible
                )
            )}
            dataSource={data?.feedbacks}
            pagination={true}
            rowSelection={rowSelection}
            pageLimit={filter.limit}
            setPagination={(pagination) =>
              handleFilter("pagination", pagination)
            }
            metaData={data?.metaData}
            isLoading={loading}
          />
        </div>
      </div>

      <DeleteModal
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
          setDeleteId(null);
          setFeedbackToDelete(null);
        }}
        itemName={selectedRowKeys.length ? "Delete" : feedbackToDelete}
        title="Delete Feedback"
        onSubmit={DeleteFeedback}
      />

      <ModalLayout
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        title="Feedback"
      >
        <div className="whitespace-pre-line p-4">{fullFeedbackText}</div>
      </ModalLayout>
    </Fragment>
  );
};

export default AllFeedbacks;
